import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import AddressAutocomplete from './AddressAutocomplete';
import PriceBreakdown from './PriceBreakdown';
import { getBookingQuote } from '../api';

const REQUIRED_FIELDS = ['line1', 'city', 'pincode'];

// Owner Bookings & Cancellation v2 doc, Story 16 - before a booking request is actually sent, the
// customer confirms (and can still correct) the delivery address they entered on the booking form
// - a last checkpoint against a typo'd pincode or an address carried over from a previous, now
// wrong, booking (see ItemDetail.js's prefill from RentalUser.savedAddress). The address search
// (delivery_ticket_pickup_drop_distance.md) also lets them pick an exact place here, which
// captures lat/lng for an accurate delivery distance.
//
// delivery_distance_pricing.md - the delivery charge is distance-based, so the address MUST carry
// coordinates (i.e. be picked from the search / "detect my location") before the booking can be
// submitted. This modal re-quotes as the address is edited and blocks "Confirm & submit" until
// the delivery charge resolves (a free-shipping listing resolves straight away at ₹0).
export default function AddressConfirmModal({
  show, address, onConfirm, onHide, submitting, itemId, startDate, endDate,
}) {
  const [draft, setDraft] = useState(address || {});
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setDraft(address || {});
      setError('');
    }
  }, [show, address]);

  useEffect(() => {
    if (!show || !itemId || !startDate || !endDate) return undefined;
    setQuoteLoading(true);
    const handle = setTimeout(() => {
      getBookingQuote({ itemId, startDate, endDate, deliveryAddress: draft })
        .then(setQuote)
        .catch(() => setQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 350);
    return () => clearTimeout(handle);
  }, [show, itemId, startDate, endDate, draft]);

  const deliveryUnresolved = quote?.deliveryChargeAvailable === false;

  const handleConfirm = () => {
    const missing = REQUIRED_FIELDS.filter((f) => !String(draft[f] || '').trim());
    if (missing.length > 0) {
      setError('Please fill in address line 1, city, and pincode.');
      return;
    }
    if (deliveryUnresolved) {
      setError('Pick your delivery address from the suggestions or use "Detect my location" so we can calculate the delivery charge.');
      return;
    }
    onConfirm(draft);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Confirm delivery address</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">Please confirm where this should be delivered before we submit your booking request.</p>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        <Form>
          <AddressAutocomplete
            value={draft}
            onChange={setDraft}
            idPrefix="address-confirm"
            required
          />
        </Form>
        {(quote || quoteLoading) && (
          <PriceBreakdown quote={quote} loading={quoteLoading && !quote} className="mt-3 pt-2 border-top" />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Back</Button>
        <Button variant="primary" onClick={handleConfirm} disabled={submitting || deliveryUnresolved}>
          {submitting ? 'Submitting…' : 'Confirm & submit booking'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
